/**
 * Fronteira unica com o libsodium.
 *
 * Todo o resto do crypto-core fala com esta interface, nunca com uma
 * biblioteca concreta. Assim o mesmo protocolo roda com
 * `libsodium-wrappers-sumo` (Node, testes, web) e com `react-native-libsodium`
 * (JSI, no aparelho) sem um `if (Platform.OS)` sequer.
 *
 * REGRA: nenhuma primitiva e implementada aqui. Este arquivo so adapta.
 */
import { CryptoNotInitializedError } from './errors.js'

export interface KeyPair {
  readonly publicKey: Uint8Array
  readonly privateKey: Uint8Array
}

/** Superficie minima do libsodium usada pelo protocolo. */
export interface SodiumBackend {
  randombytes_buf(length: number): Uint8Array

  /** Ed25519 — assinatura de identidade e de pre-chaves. */
  crypto_sign_keypair(): KeyPair
  crypto_sign_detached(message: Uint8Array, privateKey: Uint8Array): Uint8Array
  crypto_sign_verify_detached(
    signature: Uint8Array,
    message: Uint8Array,
    publicKey: Uint8Array,
  ): boolean

  /** X25519 — acordo de chaves (X3DH e ratchet DH). */
  crypto_box_keypair(): KeyPair
  crypto_scalarmult(privateKey: Uint8Array, publicKey: Uint8Array): Uint8Array

  /** BLAKE2b — toda derivacao de chave e todo fingerprint. */
  crypto_generichash(outLength: number, message: Uint8Array, key?: Uint8Array): Uint8Array

  /** XChaCha20-Poly1305-IETF — cifra autenticada, nonce de 24 bytes. */
  crypto_aead_xchacha20poly1305_ietf_encrypt(
    message: Uint8Array,
    additionalData: Uint8Array | null,
    secretNonce: null,
    publicNonce: Uint8Array,
    key: Uint8Array,
  ): Uint8Array
  crypto_aead_xchacha20poly1305_ietf_decrypt(
    secretNonce: null,
    ciphertext: Uint8Array,
    additionalData: Uint8Array | null,
    publicNonce: Uint8Array,
    key: Uint8Array,
  ): Uint8Array

  /**
   * Argon2id — deriva chave a partir de FRASE, para cifrar o cofre local.
   *
   * E a unica primitiva aqui cuja lentidao e a funcionalidade: ela existe
   * para que quem pegue o aparelho e leve o cofre nao consiga testar
   * bilhoes de frases por segundo. Nunca substituir por hash rapido.
   */
  crypto_pwhash(
    keyLength: number,
    passphrase: Uint8Array,
    salt: Uint8Array,
    opsLimit: number,
    memLimit: number,
    algorithm: number,
  ): Uint8Array

  memzero(bytes: Uint8Array): void
  memcmp(a: Uint8Array, b: Uint8Array): boolean
}

/**
 * Parametros do Argon2id, fixados no codigo e nao lidos do backend.
 *
 * Ficam aqui, e nao em `libsodium.crypto_pwhash_OPSLIMIT_*`, porque o cofre
 * precisa ser aberto com os MESMOS parametros com que foi selado: se a
 * biblioteca mudar de recomendacao numa versao futura, um cofre antigo teria
 * de continuar abrindo. Mudar qualquer numero daqui exige migracao.
 */
export const PWHASH = {
  /** crypto_pwhash_ALG_ARGON2ID13 */
  algorithm: 2,
  /** crypto_pwhash_SALTBYTES */
  saltBytes: 16,
  /**
   * "interactive" do libsodium: 2 passagens e 64 MiB.
   *
   * Nao e o parametro mais forte de proposito. O "moderate" pede 256 MiB, e
   * o libsodium compilado para WASM costuma falhar ao alocar isso em celular
   * — Safari movel em especial. Um cofre que nao abre no aparelho da pessoa
   * nao protege ninguem; protege menos que um parametro menor que funciona.
   *
   * Trocar estes numeros invalida todo cofre ja selado: a chave sai deles.
   * Se um dia subirem, tem de vir com migracao e com o parametro gravado no
   * proprio blob, nao fixado aqui.
   */
  opsLimit: 2,
  memLimit: 67_108_864,
} as const

/** Tamanhos fixados pelo libsodium. */
export const SIZES = {
  ed25519PublicKey: 32,
  ed25519PrivateKey: 64,
  ed25519Signature: 64,
  x25519PublicKey: 32,
  x25519PrivateKey: 32,
  sharedSecret: 32,
  symmetricKey: 32,
  aeadNonce: 24,
  aeadTag: 16,
  fingerprint: 32,
} as const

let backend: SodiumBackend | undefined

/**
 * Inicializa o nucleo. Sem `custom`, carrega `libsodium-wrappers-sumo`
 * (Node, testes e web). No aparelho, a Fase 4 injeta o backend JSI.
 */
export async function initCrypto(custom?: SodiumBackend): Promise<void> {
  if (custom !== undefined) {
    backend = custom
    return
  }
  const mod = await import('libsodium-wrappers-sumo')
  const s = mod.default
  await s.ready
  backend = s as unknown as SodiumBackend
}

export function sodium(): SodiumBackend {
  if (backend === undefined) throw new CryptoNotInitializedError()
  return backend
}

/** Apenas para testes: volta ao estado nao inicializado. */
export function resetCryptoForTests(): void {
  backend = undefined
}
